import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import { PageTitle } from "../../../_metronic/layout/core";
import { GenericTable } from "../generic/table/GenericTable";
import { defaultList } from "../generic/InitialStates";
import { HeaderColumnFormatter } from "../../column-formatters/HeaderColumnFormatter";
import { ActionsColumnFormatter } from "../../column-formatters/ActionsColumnFormatter";
import { ENTITY_ID_CUSTOMER_SALE, ENTITY_ID_SALES_CUSTOMER } from "../generic/Constants";
import { genericDelete } from "../generic/crud";
import { showConfirmDialog } from "../../generic-components/ConfirmDialog";
import { LoaderSlip } from "./LoaderSlip";
import { PendingEntryEditDialog } from "./PendingEntryEditDialog";
import { useAuth } from "../../modules/auth";
import { formatDateTime } from "../../utils/Utils";

export function PendingSalesPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showLoaderSlip, setShowLoaderSlip] = useState(false);
    const [loaderSlipData, setLoaderSlipData] = useState(null);
    const [editRow, setEditRow] = useState(null);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
        {
            title: "Sales",
            path: "/sales-list",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Check if record is older than 48 hours
    function isOlderThan48Hours(row) {
        if (!row.salesFirstStepDate || row.saleType !== "TWO_STEP" || !row.isPartialRecord) return false;
        const entryDate = new Date(row.salesFirstStepDate);
        const hoursDiff = (Date.now() - entryDate.getTime()) / (1000 * 60 * 60);
        return hoursDiff > 48;
    }

    // Delete stale record
    async function deleteRecord(row) {
        const confirmed = await showConfirmDialog("Are you sure you want to delete this pending sale?");
        if (!confirmed) return;
        genericDelete(ENTITY_ID_CUSTOMER_SALE, row.id).then(() => {
            setReload(prev => prev + 1);
        });
    }

    // Complete sale action
    function completeSale(row) {
        navigate(`/sale-new/edit/${row.id}`);
    }

    // Edit customer / product on a pending sale
    function editSale(row) {
        setEditRow(row);
    }

    // Print loader slip
    function printLoaderSlip(row) {
        setLoaderSlipData({
            date: row.salesFirstStepDate || new Date().toISOString(),
            vehicleNumber: row.vehicleNumber || "",
            customerName: row.salesCustomer?.name || "",
            productName: row.itemsList?.[0]?.inventoryItemName || "",
            emptyWeight: row.emptyWeight,
            operatorName: currentUser?.name || currentUser?.fullName || currentUser?.userName || "",
        });
        setShowLoaderSlip(true);
    }

    // Build columns
    const columns = useMemo(() => {
        return [
            {
                dataField: "vehicleNumber",
                text: "Vehicle #",
                search: true,
                formatter: (cell, row) => (
                    <a href="#" onClick={(e) => { e.preventDefault(); completeSale(row); }}>
                        {HeaderColumnFormatter(cell || 'N/A', row)}
                    </a>
                ),
                headerStyle: { width: '140px' }
            },
            {
                dataField: "salesCustomer.name",
                text: "Customer",
                search: true,
                formatter: (cell, row) => row.salesCustomer?.name || '-'
            },
            {
                dataField: "itemsList",
                text: "Product",
                formatter: (cell) => {
                    if (!cell || cell.length === 0) return '-';
                    // Show first item name
                    return cell[0]?.inventoryItemName || '-';
                },
            },
            {
                dataField: "emptyWeight",
                text: "Empty Weight",
                formatter: (cell) => cell ? `${cell} Ton` : '-',
                headerStyle: { width: '120px' }
            },
            {
                // Pending = a partial two-step ticket with no weigh-out yet, so it has no
                // salesSecondStepDate (the sale date) at all - the weigh-in stamp is the
                // only date it has. Display, sort and filter all stay on it here.
                dataField: "salesFirstStepDate",
                text: "Entry Date",
                formatter: (cell) => formatDateTime(cell),
                headerStyle: { width: '180px' }
            },
            {
                dataField: "action",
                text: "Actions",
                headerStyle: { width: "240px" },
                formatter: ActionsColumnFormatter,
                formatExtraData: {
                    buttons: [
                        {
                            name: "Edit Customer / Product",
                            color: "primary",
                            icon: "pencil",
                            method: editSale,
                        },
                        {
                            name: "Print Loader Slip",
                            color: "info",
                            icon: "print",
                            method: printLoaderSlip,
                        },
                        {
                            name: "Complete Sale",
                            label: "Complete",
                            color: "success",
                            method: completeSale,
                        },
                        {
                            name: "Delete",
                            color: "danger",
                            icon: "trash",
                            method: deleteRecord,
                            condition: isOlderThan48Hours,
                        },
                    ],
                },
                classes: "text-right pr-0",
                headerClasses: "text-right pr-3",
            },
        ];
    }, []);

    // Add new entry button
    function addButtonClicked() {
        navigate('/sale-step1');
    }

    useEffect(() => {
        if (myData && myData.setBaseData) {
            myData.setBaseData({
                toolBarButtons: [
                    {
                        name: "Record 2 Step Sale",
                        color: "primary",
                        icon: "fa fa-plus",
                        onClick: addButtonClicked,
                    },
                ]
            });
        }

        setPageLoadComplete(true);
    }, []);

    const BaseCard = () => (
        <div className="card card-body">
            {/* Info Banner */}
            <div className="alert alert-primary d-flex align-items-center mb-4" role="alert">
                <i className="bi bi-clock-history me-3 fs-4"></i>
                <div>
                    <strong>Pending Sales</strong> - Vehicles awaiting completion.
                    <br />
                    <small>Click "Complete" to finish the sale entry and generate the bill. Records older than 48 hours can be deleted.</small>
                </div>
            </div>

            {pageLoadComplete && (
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_CUSTOMER_SALE}
                    fixedFilterColumn="isPartialRecord"
                    fixedFilterData={true}
                    sortBy="salesFirstStepDate"
                    sortOrder="desc"
                />
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Pending Sales</PageTitle>
            <BaseCard />
            <LoaderSlip
                show={showLoaderSlip}
                slipData={loaderSlipData}
                onClose={() => setShowLoaderSlip(false)}
            />
            <PendingEntryEditDialog
                show={!!editRow}
                row={editRow}
                entityId={ENTITY_ID_CUSTOMER_SALE}
                partyField="salesCustomer"
                partyEntityId={ENTITY_ID_SALES_CUSTOMER}
                partyLabel="Customer"
                applyCustomPricing={true}
                onClose={() => setEditRow(null)}
                onSaved={() => setReload(prev => prev + 1)}
            />
        </>
    );
}
